import React, { useMemo } from "react";

type ChartDataPoint = {
  label: string;
  revenue: number;
};

type SalesChartProps = {
  data: ChartDataPoint[];
  viewMode: "day" | "week" | "month";
  title?: string;
  subtitle?: string;
};

/* =========================================================
   Helpers
   ========================================================= */

/**
 * Converts time labels into decimal hours.
 *
 * Supported examples:
 * 12 AM
 * 1 AM
 * 6 AM
 * 12 PM
 * 6 PM
 * 09:00 AM
 * 18:30
 *
 * Returns null when the label is not a time.
 */
const parseTimeToHours = (timeStr: string): number | null => {
  if (!timeStr) {
    return null;
  }

  const value = timeStr.trim().toUpperCase();

  // ---------------------------------------------
  // 12-hour format
  // Examples:
  // 12 AM
  // 1 AM
  // 09:00 AM
  // 6:30 PM
  // ---------------------------------------------

  const twelveHourMatch = value.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/);

  if (twelveHourMatch) {
    let hours = parseInt(twelveHourMatch[1], 10);

    const minutes = parseInt(twelveHourMatch[2] ?? "0", 10);

    const modifier = twelveHourMatch[3];

    if (hours < 1 || hours > 12) {
      return null;
    }

    if (modifier === "AM" && hours === 12) {
      hours = 0;
    }

    if (modifier === "PM" && hours !== 12) {
      hours += 12;
    }

    return hours + minutes / 60;
  }

  // ---------------------------------------------
  // 24-hour format
  // Examples:
  // 00:00
  // 09:00
  // 18:30
  // ---------------------------------------------

  const twentyFourHourMatch = value.match(/^(\d{1,2}):(\d{2})$/);

  if (twentyFourHourMatch) {
    const hours = parseInt(twentyFourHourMatch[1], 10);

    const minutes = parseInt(twentyFourHourMatch[2], 10);

    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      return null;
    }

    return hours + minutes / 60;
  }

  return null;
};

const formatCurrency = (value: number) => {
  return `AED ${value.toLocaleString("en-AE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const formatCompactCurrency = (value: number) => {
  return value.toLocaleString("en-AE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

/* =========================================================
   Component
   ========================================================= */

const SalesChart: React.FC<SalesChartProps> = ({
  data = [],
  viewMode,
  title = "Sales Analytics",
  subtitle = "Overview",
}) => {
  /* =====================================================
       Validate today's data

       Today requires actual time-based labels.

       Example:
       12 AM
       1 AM
       2 AM
       ...

       If the API still returns:
       Mon
       Tue
       Wed

       we should NOT incorrectly draw those values as
       today's hourly sales.
       ===================================================== */

  const hasHourlyData = useMemo(() => {
    if (viewMode !== "day") {
      return true;
    }

    return data.some((item) => parseTimeToHours(item.label) !== null);
  }, [data, viewMode]);

  /* =====================================================
       Maximum revenue
       ===================================================== */

  const maxDataValue = Math.max(
    ...data.map((item) => Number(item.revenue) || 0),
    0,
  );

  const chartMax = maxDataValue > 0 ? Math.ceil(maxDataValue * 1.1) : 100;

  /* =====================================================
       Y Axis
       ===================================================== */

  const yAxisTicks = Array.from({ length: 6 }, (_, index) =>
    Math.round(chartMax - (chartMax / 5) * index),
  );

  /* =====================================================
       SVG dimensions
       ===================================================== */

  const svgWidth = 1000;
  const svgHeight = 320;

  const paddingLeft = 20;
  const paddingRight = 20;
  const paddingTop = 30;
  const paddingBottom = 35;

  const graphWidth = svgWidth - paddingLeft - paddingRight;

  const graphHeight = svgHeight - paddingTop - paddingBottom;

  /* =====================================================
       Prepare day data

       If backend returns hourly data:

       12 AM
       1 AM
       2 AM
       ...

       use the actual hour.

       Otherwise don't draw misleading data.
       ===================================================== */

  const hourlyData = useMemo(() => {
    if (viewMode !== "day" || !hasHourlyData) {
      return [];
    }

    return data
      .map((item, index) => ({
        ...item,
        hour: parseTimeToHours(item.label),
        originalIndex: index,
      }))
      .filter((item) => item.hour !== null)
      .sort((a, b) => (a.hour ?? 0) - (b.hour ?? 0));
  }, [data, viewMode, hasHourlyData]);

  /* =====================================================
       Coordinates
       ===================================================== */

  const getDayCoordinates = (
    item: ChartDataPoint & {
      hour: number | null;
    },
  ) => {
    const hour = item.hour ?? 0;

    const x = paddingLeft + (hour / 24) * graphWidth;

    const revenue = Number(item.revenue) || 0;

    const heightRatio = chartMax > 0 ? revenue / chartMax : 0;

    const y = paddingTop + graphHeight - heightRatio * graphHeight;

    return {
      x,
      y,
    };
  };

  /* =====================================================
       Day line path
       ===================================================== */

  const dayLinePath =
    hourlyData.length > 0
      ? hourlyData
          .map((item, index) => {
            const { x, y } = getDayCoordinates(item);

            return `${index === 0 ? "M" : "L"} ${x} ${y}`;
          })
          .join(" ")
      : "";

  /* =====================================================
       X-axis day labels
       ===================================================== */

  const dayAxisLabels = [
    {
      label: "12 AM",
      position: 0,
    },
    {
      label: "06 AM",
      position: 6,
    },
    {
      label: "12 PM",
      position: 12,
    },
    {
      label: "06 PM",
      position: 18,
    },
    {
      label: "12 AM",
      position: 24,
    },
  ];

  /* =====================================================
       Render
       ===================================================== */

  return (
    <div
      className="
                bg-white
                rounded-2xl
                p-4
                sm:p-6
                shadow-sm
                border
                border-gray-100
                overflow-hidden
                h-full
            "
    >
      {/* =================================================
                HEADER
               ================================================= */}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-6">
        <div>
          <h2
            className="
                            text-lg
                            sm:text-xl
                            font-bold
                            text-logoHeading
                        "
          >
            {title}
          </h2>

          <p
            className="
                            text-sm
                            text-gray-500
                            mt-1
                        "
          >
            {subtitle}
          </p>
        </div>
      </div>

      {/* =================================================
                TODAY
               ================================================= */}

      {viewMode === "day" ? (
        !hasHourlyData ? (
          <div
            className="
                            h-[260px]
                            sm:h-[320px]
                            flex
                            items-center
                            justify-center
                            text-center
                            px-6
                        "
          >
            <div>
              <div
                className="
                                    text-sm
                                    font-semibold
                                    text-gray-500
                                "
              >
                No hourly sales data available
              </div>

              <div
                className="
                                    text-xs
                                    text-gray-400
                                    mt-1
                                "
              >
                Today's hourly sales will appear here once the dashboard API
                returns hourly data.
              </div>
            </div>
          </div>
        ) : (
          <div className="relative">
            {/* =========================================
                            GRAPH
                           ========================================= */}

            <div
              className="
                                relative
                                h-[260px]
                                sm:h-[320px]
                            "
            >
              {/* Y AXIS */}

              <div
                className="
                                    absolute
                                    left-0
                                    top-0
                                    bottom-8
                                    w-12
                                    flex
                                    flex-col
                                    justify-between
                                    text-xs
                                    text-gray-400
                                    text-right
                                    pr-2
                                "
              >
                {yAxisTicks.map((tick, index) => (
                  <span key={index}>{formatCompactCurrency(tick)}</span>
                ))}
              </div>

              {/* GRAPH */}

              <div
                className="
                                    absolute
                                    left-14
                                    right-2
                                    top-0
                                    bottom-0
                                "
              >
                {/* GRID */}

                <div
                  className="
                                        absolute
                                        inset-0
                                        bottom-8
                                        flex
                                        flex-col
                                        justify-between
                                    "
                >
                  {yAxisTicks.map((_, index) => (
                    <div
                      key={index}
                      className="
                                                    border-t
                                                    border-dashed
                                                    border-gray-200
                                                    w-full
                                                "
                    />
                  ))}
                </div>

                {/* SVG */}

                <svg
                  viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                  className="
                                        absolute
                                        inset-0
                                        w-full
                                        h-full
                                        overflow-visible
                                    "
                  preserveAspectRatio="none"
                >
                  {/* LINE */}

                  {hourlyData.length >= 2 && (
                    <path
                      d={dayLinePath}
                      fill="none"
                      stroke="#6DBE45"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      vectorEffect="non-scaling-stroke"
                    />
                  )}

                  {/* POINTS */}

                  {hourlyData.map((item, index) => {
                    const { x, y } = getDayCoordinates(item);

                    /*
                     * When there are many
                     * hourly points, displaying
                     * every revenue value causes
                     * overlapping labels.
                     *
                     * Therefore:
                     *
                     * <= 10 points:
                     * show values
                     *
                     * > 10 points:
                     * show circles only
                     */

                    const showValue = hourlyData.length <= 10;

                    return (
                      <g key={`${item.label}-${index}`}>
                        <circle
                          cx={x}
                          cy={y}
                          r="5"
                          fill="#6DBE45"
                          stroke="#FFFFFF"
                          strokeWidth="2"
                          vectorEffect="non-scaling-stroke"
                        />

                        {showValue && (
                          <text
                            x={x}
                            y={y}
                            dy="-12"
                            textAnchor="middle"
                            className="
                                                                text-[11px]
                                                                font-bold
                                                                fill-logoHeading
                                                            "
                          >
                            {formatCompactCurrency(item.revenue)}
                          </text>
                        )}
                      </g>
                    );
                  })}
                </svg>

                {/* =================================
                                    TIME AXIS
                                   ================================= */}

                <div
                  className="
                                        absolute
                                        left-0
                                        right-0
                                        bottom-0
                                        h-6
                                    "
                >
                  {dayAxisLabels.map((item) => (
                    <span
                      key={`${item.position}-${item.label}`}
                      className="
                                                    absolute
                                                    -translate-x-1/2
                                                    text-[10px]
                                                    sm:text-xs
                                                    text-gray-400
                                                    font-medium
                                                    whitespace-nowrap
                                                "
                      style={{
                        left: `${(item.position / 24) * 100}%`,
                      }}
                    >
                      {item.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )
      ) : (
        /* =================================================
                   WEEK / MONTH
                   ================================================= */

        <div
          className="
                        relative
                        h-[260px]
                        sm:h-[320px]
                    "
        >
          {/* Y AXIS */}

          <div
            className="
                            absolute
                            left-0
                            top-0
                            bottom-8
                            w-12
                            flex
                            flex-col
                            justify-between
                            text-xs
                            text-gray-400
                            text-right
                            pr-2
                        "
          >
            {yAxisTicks.map((tick, index) => (
              <span key={index}>{formatCompactCurrency(tick)}</span>
            ))}
          </div>

          {/* GRAPH */}

          <div
            className="
                            absolute
                            left-14
                            right-2
                            top-0
                            bottom-0
                        "
          >
            {/* GRID */}

            <div
              className="
                                absolute
                                inset-0
                                bottom-8
                                flex
                                flex-col
                                justify-between
                            "
            >
              {yAxisTicks.map((_, index) => (
                <div
                  key={index}
                  className="
                                            border-t
                                            border-dashed
                                            border-gray-200
                                            w-full
                                        "
                />
              ))}
            </div>

            {/* BARS */}

            <div
              className="
                                absolute
                                inset-0
                                bottom-0
                                flex
                                items-end
                                justify-between
                                gap-2
                                sm:gap-4
                            "
            >
              {data.map((item, index) => {
                const revenue = Number(item.revenue) || 0;

                const percentageHeight =
                  chartMax > 0 ? (revenue / chartMax) * 100 : 0;

                const barHeight =
                  revenue > 0 ? Math.max(percentageHeight * 0.82, 2) : 0;

                return (
                  <div
                    key={`${item.label}-${index}`}
                    className="
                                                flex
                                                flex-col
                                                items-center
                                                justify-end
                                                h-full
                                                flex-1
                                                min-w-[35px]
                                            "
                  >
                    {/* VALUE */}

                    <span
                      className="
                                                    text-[10px]
                                                    sm:text-xs
                                                    font-medium
                                                    text-logoHeading
                                                    mb-2
                                                    whitespace-nowrap
                                                "
                    >
                      {formatCompactCurrency(revenue)}
                    </span>

                    {/* BAR */}

                    <div
                      className="
                                                    w-full
                                                    max-w-[50px]
                                                    rounded-t-[5px]
                                                    bg-[#6DBE45]
                                                    hover:scale-105
                                                    transition-all
                                                    duration-300
                                                    shadow-sm
                                                "
                      style={{
                        height: `${barHeight}%`,
                      }}
                    />

                    {/* LABEL */}

                    <span
                      className="
                                                    text-[11px]
                                                    sm:text-xs
                                                    text-gray-500
                                                    mt-4
                                                    font-medium
                                                    whitespace-nowrap
                                                "
                    >
                      {item.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesChart;
